import { RelatedServiceItem } from '@/app/models/detailsRelatedService';
import { FinancialGroup, SubGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import {
  useSearchRelatedServiceQuery,
  useUnitDetailsRelatedServiceMutation,
} from '@/app/services/detailsRelatedServiceApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, FormControl, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import NewEditTransferCost from '../NewEditTransferCost';
import NewEditRelatedServiceDetails from './NewEditRelatedServiceDetails';
import RelatedServiceTable from './RelatedServiceTable';

interface EquipmentProps {
  group: FinancialGroup;
}

function RelatedService(props: EquipmentProps) {
  const theme = useTheme();
  const { code } = useParams();
  const period = usePeriod();
  const { t } = useTranslation(['relatedService', 'common']);

  const { group } = props;

  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawerTransfer, setDrawerTransfer] = useState(false);

  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });
  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();

  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [selectRelatedService, setSelectRelatedService] = useState<number[]>([]);

  const [originSupplier, setOriginSupplier] = useState<RelatedServiceItem | null>(null);
  const [originSupplierSearch, setOriginSupplierSearch] = useState('');

  const { data: relatedService } = useSearchRelatedServiceQuery(
    {
      period: period.selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originSupplierSearch || '',
    },
    {
      skip: !period.selectedPeriod?.date || !code || !selectedFinancialSubGroup,
    },
  );

  const [selectRelatedServiceItem, setSelectedRelatedServiceItem] = useState<RelatedServiceItem>();

  const [unit] = useUnitDetailsRelatedServiceMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectRelatedService,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectRelatedService([]);
  };

  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();

  const { selectedFinancialGroup } = useFinancialGroup();

  useEffect(() => {
    if (!selectRelatedServiceItem || !selectedFinancialSubGroup || !period.selectedPeriod) return;

    setSelectedItem({
      id: selectRelatedServiceItem.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: period.selectedPeriod.date,
      complement: selectRelatedServiceItem.complement,
      newDetail: {
        description: selectRelatedServiceItem.description,
        value: selectRelatedServiceItem.value,
        supplier: selectRelatedServiceItem.supplier || '',
      },
    });
  }, [selectedFinancialSubGroup, period.selectedPeriod, selectedFinancialGroup, selectRelatedServiceItem]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ minWidth: theme.spacing(31) }}>
              <Typography component="p" variant="fieldHeader">
                {t('common:group')}
              </Typography>
              <FormControl sx={{ width: theme.spacing(30) }}>
                <Select
                  displayEmpty
                  value={selectedFinancialSubGroup?.id || ({} as SubGroup)}
                  onChange={(e) => {
                    const selected = financialsubGroup?.find((item) => item.id === e.target.value);
                    if (selected) setSelectedFinancialSubGroup(selected);
                  }}
                  sx={{ height: 32, fontSize: 14 }}
                >
                  {financialsubGroup &&
                    financialsubGroup.map((item: SubGroup) => (
                      <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                        {`${item.name}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originSupplier}
              onChange={(value) => {
                setOriginSupplier(value);
              }}
              equalityTest={(option, value) => option?.supplier === value?.supplier}
              getOptionLabel={(option) => (option ? `${option.supplier ?? ''} - ${option.supplierCode ?? ''}` : '')}
              onSearch={(value) => {
                setOriginSupplierSearch(value);
              }}
              options={[]}
              placeholder={t('common:type.to.search') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>

          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(relatedService?.totalValue || null)}</Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectRelatedService.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <RelatedServiceTable
        selectRelatedService={selectRelatedService}
        setSelectRelatedService={setSelectRelatedService}
        setDrawerEdit={setDrawerEdit}
        setSelectedRelatedServiceItem={setSelectedRelatedServiceItem}
        selectRelatedServiceItem={selectRelatedServiceItem as RelatedServiceItem}
        setDrawerTransfer={setDrawerTransfer}
        equipmentData={relatedService}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditRelatedServiceDetails
          selectItem={selectRelatedServiceItem as RelatedServiceItem}
          setDrawerOpen={setDrawerEdit}
        />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default RelatedService;
